import SingleLineText from './ui/SingleLineText';
import React from 'react';
import { Box, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import UndoIcon from '@mui/icons-material/Undo';
import MedImage from './MedImage';
import { FONT_FAMILY as FONT_STACK } from '../design/tokens';
import useAppLanguage from '../i18n';

function isOral(intakeForm) {
    return !intakeForm || intakeForm === 'ORAL';
}

function completeLabel(meds, t) {
    const list = Array.isArray(meds) ? meds : [];
    const allOral = list.every(m => isOral(m.intakeForm));
    const allExternal = list.length > 0 && list.every(m => !isOral(m.intakeForm));
    if (allOral) return { noun: t('복용'), done: t('복용 완료') };
    if (allExternal) return { noun: t('사용'), done: t('사용 완료') };
    return { noun: t('완료'), done: t('완료') };
}

export default function DoneGroupCard({ time, count, scheduledTime, meds, onEditOne, onEditAll, onUndoAll }) {
    const { t } = useAppLanguage();
    const label = completeLabel(meds, t);
    const undoLabel = count === 1 ? t('완료 취소') : t('{count}개 모두 되돌리기', { count });

    return (
        <Box
            data-tappable="true"
            onClick={onEditAll}
            sx={{
                bgcolor: '#F4FAF5', border: '1px solid #D4ECDA', borderRadius: 4, p: 2,
                fontFamily: FONT_STACK, width: '100%', mb: 1.5, cursor: 'pointer',
                '&:active': { bgcolor: '#EFF8F1' },
            }}>
            {/* 1. 헤더 — PENDING 그룹 카드와 동일한 시각/텍스트 위계 */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 1.2 }}>
                <Box sx={{
                    width: 22, height: 22, borderRadius: '50%', bgcolor: '#1F8A3D',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                    <CheckIcon sx={{ fontSize: 16, color: '#fff' }} />
                </Box>
                <Typography sx={{
                    fontWeight: 800, fontSize: '1.15rem', color: '#1F8A3D',
                    fontVariantNumeric: 'tabular-nums', fontFamily: FONT_STACK,
                }}>{time}</Typography>
                <Typography sx={{
                    fontSize: '1.05rem', color: '#7A766F', fontFamily: FONT_STACK,
                }}>· {t('{count}개 {state}', { count, state: label.done })}</Typography>
            </Box>

            {/* 2. 요약 한 줄 */}
            <Typography sx={{
                fontSize: '0.95rem', color: '#7A766F', mt: -0.5, mb: 1.2,
                fontFamily: FONT_STACK, fontVariantNumeric: 'tabular-nums',
                pl: '30px',
            }}>{t('예정 시간 {time}', { time: scheduledTime || '—' })}</Typography>

            {/* 3. 약 리스트 — PENDING과 동일한 행 사이즈 (이미지 32, 약 이름 1.05rem, chevron 20) */}
            <Box sx={{
                bgcolor: '#fff', border: '1px solid #D4ECDA',
                borderRadius: 3, overflow: 'hidden', mb: 1.2,
            }}>
                {meds.map((m, idx) => (
                    <Box
                        key={m.id}
                        component="button"
                        type="button"
                        aria-label={t('{name} 시간 수정', { name: m.name })}
                        onClick={(e) => { e.stopPropagation(); onEditOne(m.id); }}
                        sx={{
                            display: 'flex', flexWrap: 'wrap', alignItems: 'center', columnGap: 1.2, rowGap: '4px',
                            width: '100%', py: 1, px: 1.5,
                            border: 'none',
                            borderTop: idx === 0 ? 'none' : '1px solid #E6F1E9',
                            bgcolor: 'transparent', cursor: 'pointer',
                            textAlign: 'left', color: 'inherit',
                            fontFamily: FONT_STACK,
                            '&:active': { bgcolor: '#F1FBF3' },
                            '&:focus-visible': { outline: '2px solid #1F8A3D', outlineOffset: '-2px' },
                        }}
                    >
                        <MedImage src={m.imageUrl} size={32} />
                        <Box sx={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                            <Typography sx={{
                                fontWeight: 600, fontSize: '1.05rem', color: '#1a1a1a',
                                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                fontFamily: FONT_STACK,
                            }}>{m.name}</Typography>
                        </Box>
                        <Typography sx={{
                            fontSize: '1.05rem', fontWeight: 700, color: '#1F8A3D',
                            fontVariantNumeric: 'tabular-nums', fontFamily: FONT_STACK,
                            flexShrink: 0,
                        }}>{m.takenAt}</Typography>
                        <ChevronRightIcon sx={{ fontSize: 20, color: '#A8A39B', flexShrink: 0 }} />
                        {m.guardianNickname && (
                            <Box sx={{ width: '100%' }}>
                                <Box sx={{
                                    display: 'inline-flex', alignItems: 'center', whiteSpace: 'nowrap',
                                    bgcolor: '#FFF7E0', border: '1px solid #F3E2A8',
                                    borderRadius: '999px', px: '8px', py: '2px',
                                }}>
                                    <Typography sx={{
                                        fontSize: '0.78rem', fontWeight: 700, color: '#8a7000',
                                        fontFamily: FONT_STACK,
                                    }}>
                                        {t('{name}님이 챙겨드렸어요', { name: m.guardianNickname })}
                                    </Typography>
                                </Box>
                            </Box>
                        )}
                    </Box>
                ))}
            </Box>

            {/* 4. 가이드 캡션 */}
            <SingleLineText sx={{
                fontSize: '0.9rem', color: '#7A766F', textAlign: 'center',
                pt: 0.3, pb: 1.2, fontFamily: FONT_STACK,
            }}>{t('약을 눌러 시간을 따로 수정할 수도 있어요')}</SingleLineText>

            {/* 5. Primary 버튼 — PENDING 완료 버튼과 동일 사이즈 */}
            <Box
                component="button"
                type="button"
                aria-label={t('시간 한 번에 변경')}
                onClick={(e) => { e.stopPropagation(); onEditAll(); }}
                sx={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75,
                    width: '100%', bgcolor: '#1F8A3D', color: '#fff',
                    border: 'none', borderRadius: 3,
                    py: 1.8, px: 0,
                    fontSize: '1.1rem', fontWeight: 700, fontFamily: FONT_STACK,
                    cursor: 'pointer',
                    boxShadow: '0 2px 8px rgba(31,138,61,0.25)',
                    '&:active': { bgcolor: '#176F30', transform: 'scale(0.98)' },
                    '&:focus-visible': { outline: '2px solid #176F30', outlineOffset: '2px' },
                }}
            >
                <AccessTimeIcon sx={{ fontSize: 18, color: '#fff' }} />
                {t('시간 변경')}
            </Box>

            {/* 6. 보조 액션 — onUndoAll 없으면 노출 안 함 (예: 과거 날짜 카드) */}
            {onUndoAll && (
                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                    <Box
                        component="button"
                        type="button"
                        aria-label={undoLabel}
                        onClick={(e) => { e.stopPropagation(); onUndoAll(); }}
                        sx={{
                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5,
                            width: '100%', bgcolor: '#F1FBF3', color: '#236B36',
                            border: '1px solid #D4ECDA', borderRadius: 3,
                            py: 1.5, px: 0,
                            fontSize: '1.05rem', fontWeight: 600, fontFamily: FONT_STACK,
                            cursor: 'pointer',
                            '&:active': { bgcolor: '#E6F4EA' },
                            '&:focus-visible': { outline: '2px solid #236B36', outlineOffset: '2px' },
                        }}
                    >
                        <UndoIcon sx={{ fontSize: 16, color: '#236B36' }} />
                        {undoLabel}
                    </Box>
                </Box>
            )}
        </Box>
    );
}
